import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, useToast, Notice,
} from '@kollez/design-system';
import { useMockSession, useSubmitMockAnswer, useFinishMock } from '@/lib/queries';

/**
 * /mock/:id — the session runner. Presents one question at a time; grades on submit; wraps into
 * the report card at the end.
 */
export function StudentMockSessionPage() {
  const { id = '' } = useParams();
  const { data, isLoading, error, refetch } = useMockSession(id);
  const submit = useSubmitMockAnswer(id);
  const finish = useFinishMock(id);
  const toast = useToast();
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState('');
  // Deep-audit fix (Page 28/N): hints are structural coaching ('Use STAR',
  // 'name a metric'). Showing them pre-answer defeats a chunk of the
  // practice value — the student sees the scaffolding they'd have to
  // build themselves in a real interview. Now hidden by default; a
  // toggle reveals them when the student is stuck.
  const [showHints, setShowHints] = useState(false);

  const session = data?.session;
  const report = data?.report;
  // F-091: `answers` may be absent from freshly-loaded sessions or when the transport is stub.
  // Same for `questions` when the session hasn't been fully materialised yet.
  const q = session?.questions?.[idx];
  const a = session?.answers?.[idx];

  async function grade() {
    if (!draft.trim()) { toast.show({ tone: 'warning', message: 'Type an answer first.' }); return; }
    try {
      await submit.mutateAsync({ questionIndex: idx, answer: draft });
      setDraft('');
      toast.show({ message: 'Graded' });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not grade.' });
    }
  }

  async function complete() {
    try {
      const r = await finish.mutateAsync();
      toast.show({ message: `Report ready · ${r.report.overallScore}/100` });
    } catch {
      toast.show({ tone: 'warning', message: 'Could not finish the session.' });
    }
  }

  return (
    <AsyncBoundary kind="detail" loading={isLoading} error={error} empty={!isLoading && !session}
      emptyTitle="Session not found" onRetry={() => void refetch()}>
      {session && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">Mock · {session.targetRole} · {session.difficulty}</p>
              <h1 className="h-page">Session {session.status === 'completed' ? 'complete' : 'in progress'}</h1>
            </div>
            <div className="ph-actions">
              <Button variant="ghost" onClick={() => nav('/mock')}>← All sessions</Button>
              {session.status === 'in_progress' && (
                <Button variant="primary" onClick={complete} loading={finish.isPending}>Finish &amp; get report</Button>
              )}
            </div>
          </div>

          {report ? <MockReportCard report={report} /> : null}

          {session.status === 'in_progress' && q && (
            <>
              <div className="flex flex-wrap gap-2">
                {session.questions.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setIdx(i); setDraft(''); }}
                    className={`px-3 py-1 rounded text-sm ${i === idx ? 'bg-bg-info text-ink-inverse' : 'bg-bg-secondary text-ink-secondary'}`}
                  >
                    Q{i + 1}
                    {session.answers?.[i]?.score !== undefined && <span className="ml-1 opacity-70">· {session.answers?.[i]?.score}</span>}
                  </button>
                ))}
              </div>

              <Card padding="lg">
                <div className="flex items-baseline gap-2 mb-2">
                  <Badge tone="info">{q.kind}</Badge>
                  <Badge tone="outline">{q.difficulty}</Badge>
                </div>
                <div className="font-serif text-2xl leading-snug mb-4">{q.question}</div>

                {a?.score !== undefined ? (
                  <div className="grid gap-3">
                    <Notice tone="info">
                      Nexie graded this: <b className="text-ink-primary">{a.score}/100</b> · {a.feedback}
                    </Notice>
                    {a.strengths && a.strengths.length > 0 && (
                      <div className="text-sm"><b className="text-ink-tertiary">Strengths:</b> {a.strengths.join(' · ')}</div>
                    )}
                    {a.gaps && a.gaps.length > 0 && (
                      <div className="text-sm"><b className="text-ink-tertiary">Gaps:</b> {a.gaps.join(' · ')}</div>
                    )}
                    <div className="flex gap-2 mt-2">
                      {idx < session.questions.length - 1 ? (
                        <Button variant="ghost" onClick={() => { setIdx(idx + 1); setShowHints(false); }}>Next question →</Button>
                      ) : (
                        // Last question graded — surface Finish here so the
                        // student doesn't have to scroll up to the header CTA.
                        <Button variant="primary" onClick={complete} loading={finish.isPending}>Finish &amp; get report</Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={8}
                      className="input w-full"
                      placeholder="Type your answer. 80-150 words is a strong length for behavioural, longer for technical."
                    />
                    <div className="flex justify-between mt-2 gap-3 flex-wrap items-center">
                      <div className="text-xs text-ink-tertiary self-center">
                        {q.idealAnswerHints.length > 0 && (
                          showHints
                            ? <>Hints: {q.idealAnswerHints.join(' · ')} · <button className="underline" onClick={() => setShowHints(false)}>hide</button></>
                            : <button className="underline" onClick={() => setShowHints(true)}>Show hints ({q.idealAnswerHints.length})</button>
                        )}
                      </div>
                      <Button variant="primary" onClick={grade} loading={submit.isPending}>Submit for grading</Button>
                    </div>
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      )}
    </AsyncBoundary>
  );
}

function MockReportCard({ report }: { report: NonNullable<ReturnType<typeof useMockSession>['data']>['report'] }) {
  if (!report) return null;
  const tone = report.overallScore >= 75 ? 'success' : report.overallScore >= 55 ? 'info' : 'warning';
  return (
    <Card padding="lg">
      <div className="flex items-baseline gap-4 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-tertiary">Overall</div>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-5xl leading-none">{report.overallScore}</span>
            <span className="text-ink-tertiary text-lg">/100</span>
            <Badge tone={tone as 'success' | 'info' | 'warning'}>
              {report.overallScore >= 75 ? 'Ready' : report.overallScore >= 55 ? 'On track' : 'Keep practising'}
            </Badge>
          </div>
        </div>
        {typeof report.deltaVsPrevious === 'number' && (
          <div className="text-sm">
            <span className="text-ink-tertiary">vs last:</span>{' '}
            <b className={report.deltaVsPrevious >= 0 ? 'text-ink-success' : 'text-ink-warning'}>
              {report.deltaVsPrevious > 0 ? '+' : ''}{report.deltaVsPrevious}
            </b>
          </div>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <ReportBlock title="Strengths" tone="success" items={report.strengths} />
        <ReportBlock title="Gaps" tone="warning" items={report.gaps} />
        <ReportBlock title="Next steps" tone="info" items={report.suggestedNextSteps} />
      </div>
    </Card>
  );
}

function ReportBlock({ title, tone, items }: { title: string; tone: 'success' | 'warning' | 'info'; items: string[] }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-ink-tertiary mb-1">{title}</div>
      <ul className="grid gap-1 text-sm">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <Badge tone={tone}>·</Badge>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
